import { X } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  /** Max panel width. */
  size?: 'sm' | 'md' | 'lg'
  /** Remove the default body padding (media viewers, chat panes). */
  bare?: boolean
}

const sizeClass = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }

/** Centered dialog with backdrop + Escape close. Panels scroll internally. */
export function Modal({ open, onClose, title, children, size = 'md', bare = false }: ModalProps) {
  const { t } = useTranslation('common')
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    // Lock the page scroll behind the dialog.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-canvas border-border flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border shadow-2xl sm:rounded-2xl ${sizeClass[size]}`}
      >
        {title !== undefined && (
          <div className="border-border flex items-center justify-between gap-4 border-b px-5 py-3.5">
            <h2 className="text-fg text-base font-semibold">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-fg-subtle hover:text-fg -mr-1 rounded-md p-1 transition"
              aria-label={t('actions.close')}
            >
              <X className="size-5" />
            </button>
          </div>
        )}
        <div className={`min-h-0 flex-1 overflow-y-auto ${bare ? '' : 'p-5'}`}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}
